

export type PixelsStruct = {
  data: Uint8Array,
  shape: number[],
  stride: number[]
}

export class GetPixels {
  public static getPixels(b64_image: string): Promise<PixelsStruct> {
    return new Promise((ok, err) => {
      let img = new Image();
      img.crossOrigin = "Anonymous";
      img.onload = () => {
        let canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        let context = canvas.getContext('2d');
        if (context) {
          context.drawImage(img, 0, 0);
          let pixels = context.getImageData(0, 0, img.width, img.height);
          ok({ data: new Uint8Array(pixels.data), shape: [img.width, img.height, 4], stride: [4, 4 * img.width, 1] });
        }
      }
      img.onerror = (e) => { err(e); }
      img.src = b64_image;
    });
  }
}

export class ESCPOSImageSize {
  public width: number = 0;
  public height: number = 0;
  public colors: number = 0;

  constructor(width?: number, height?: number, colors?: number) {
    if (width)
      this.width = width;
    if (height)
      this.height = height;
    if (colors)
      this.colors = colors;
  }
}

type ESCPOSImagePixel = {
  r: number,
  g: number,
  b: number,
  a: number
};

export class ESCPOSImage {
  public pixels?: PixelsStruct;
  public data: number[] = [];

  private rgb(pixel: number[]): ESCPOSImagePixel {
    return {
      r: pixel[0],
      g: pixel[1],
      b: pixel[2],
      a: pixel[3]
    };
  }

  constructor(pixels: PixelsStruct) {
    this.pixels = pixels;
    if (!this.size)
      throw "Invalid size";
    let temp_arr: ESCPOSImagePixel[] = [];
    for (var i = 0; i < this.pixels.data.length; i += this.size.colors) {
      let color_arr = new Array(this.size.colors).fill(0);
      let d = this.rgb(color_arr.map((_, b) => this.pixels!.data[i + b]));
      temp_arr.push(d);
    };

    this.data = temp_arr.map((pixel) => {
      if (pixel.a == 0) return 0;
      return pixel.r !== 0xFF || pixel.g !== 0xFF || pixel.b !== 0xFF ? 1 : 0;
    });
  }

  public static load(b64_image: string): Promise<ESCPOSImage> {
    return new Promise<ESCPOSImage>((ok, err) => {
      GetPixels.getPixels(b64_image)
        .then((value: PixelsStruct) => {
          ok(new ESCPOSImage(value));
        })
        .catch((reason) => {
          err(reason);
        });
    });
  }

  get size(): ESCPOSImageSize | undefined {
    if (!this.pixels)
      return undefined;
    return new ESCPOSImageSize(this.pixels.shape[0], this.pixels.shape[1], this.pixels.shape[2]);
  }

  public toBitmap(density: number) {
    if (!this.size)
      return undefined;

    density = density || 24;

    var ld: number[] = [];
    var result: number[][] = [];
    var x: number, y: number, b: number, l: number, i: number;
    var c = density / 8;
    var n = Math.ceil(this.size.height / density);

    for (y = 0; y < n; y++) {
      ld = [];
      result[y] = [];
      for (x = 0; x < this.size.width; x++) {

        for (b = 0; b < density; b++) {
          i = x * c + (b >> 3);

          if (ld[i] === undefined) {
            ld[i] = 0;
          }

          l = y * density + b;
          if (l < this.size.height) {
            if (this.data[l * this.size.width + x]) {
              ld[i] += (0x80 >> (b & 0x7));
            }
          }
        }
      }
      result[y] = ld;
    }

    return {
      data: result,
      density: density
    };
  }

  public toRaster() {
    if (!this.size)
      return undefined;
    var result = [];
    var width = this.size.width;
    var height = this.size.height;
    var data = this.data;

    // n blocks of lines
    var n = Math.ceil(width / 8);
    var x: number, y: number, b: number, c: number, i: number;

    for (y = 0; y < height; y++) {

      for (x = 0; x < n; x++) {

        for (b = 0; b < 8; b++) {
          i = x * 8 + b;

          if (result[y * n + x] === undefined) {
            result[y * n + x] = 0;
          }

          c = x * 8 + b;
          if (c < width) {
            if (data[y * width + i]) {
              result[y * n + x] += (0x80 >> (b & 0x7));
            }
          }
        }
      }
    }
    return {
      data: result,
      width: n,
      height: height
    };
  }
}

export enum BitmapDensity {
  S8,
  D8,
  S24,
  D24
}

export class LineSpacing {
  static readonly DEFAULT: number[] = [27, 50];
  static readonly SET: number[] = [27, 51];
}

export class PrintBuffer {
  private _buffer: number[] = [];
  public encoding = 858;

  private _addB(value: number[][]) {
    for (var i = 0; i < value.length; i++)
      this._buffer = this._buffer.concat(value[i]);
  }

  private _wrapperSend2DCodeData(func: number, code_type: number, data?: any, modifier?: number) {
    let buf: number[][] = [];
    let header = Utils.int16ToArray((data != undefined ? data.length : 0) + (modifier != undefined? 1 : 0) + 2);
    buf = [Constants.GS, [40, 107], header, [code_type, func]];
    if(modifier != undefined)
      buf.push([modifier]);
    if(typeof(data) == 'string')
      buf.push(cptable.utils.encode(this.encoding, data));
    else
      buf.push(data);
    this._addB(buf);
  }

  public qrCode(code: string, options: BarcodeQROptions = new BarcodeQROptions()): any {
    let ct = 49;
    this._wrapperSend2DCodeData(65, ct, [50, 0]);       // Model QR 2
    this._wrapperSend2DCodeData(67, ct, [options.size]);        // Size
    this._wrapperSend2DCodeData(69, ct, [48 + options.level]);  // Error correction level
    this._wrapperSend2DCodeData(80, ct, code, 48);      // Send content & print
    this._wrapperSend2DCodeData(81, ct, '', 48);        
  }

  public lineSpacing(value?: number): any {
    let buf: number[][] = [];
    if (value || value === 0) {
      if (!Utils.isValidUInt8(value)) throw "Invalid value";
      buf.push(LineSpacing.SET)
      buf.push([value]);
    } else {
      buf.push(LineSpacing.DEFAULT);
    }
    this._addB(buf);
  }

  public image(image: ESCPOSImage, density: BitmapDensity = BitmapDensity.D24): any {
    let n = 3;
    if ([BitmapDensity.D8, BitmapDensity.S8].indexOf(density) > -1)
      n = 1;
    let bitmap = image.toBitmap(n * 8);
    let header = [];
    switch (density) {
      case BitmapDensity.S8: {
        header = BitmapFormat.S8;
      } break;
      case BitmapDensity.D8: {
        header = BitmapFormat.D8;
      } break;
      case BitmapDensity.S24: {
        header = BitmapFormat.S24;
      } break;
      case BitmapDensity.D24: {
        header = BitmapFormat.D24;
      } break;
    }
    this.lineSpacing(0);
    let buf: number[][] = [];
    if (bitmap) {
      for (var i = 0; i < bitmap.data.length; i++) {
        let line = bitmap.data[i];
        buf.push(header);
        buf.push(Utils.int16ToArray(line.length / n));
        buf.push(line);
        buf.push(Constants.EOL);
      }
    }
    this._addB(buf);
  }

  public generateUInt8Array(): Uint8Array {
    return new Uint8Array(this._buffer);
  }

  public generateArray(): number[] {
    return this._buffer;
  }

  public toHextString(): string {
    return Array.from(this._buffer, function (byte) {
      let sHex = "0x" + ('0' + (byte & 0xFF).toString(16)).slice(-2)
      return sHex;
    }).join(' ')
  }
}

export class Constants {
  static readonly LF: number[] = [10];
  static readonly FS: number[] = [28];
  static readonly FF: number[] = [12];
  static readonly GS: number[] = [29];
  static readonly DLE: number[] = [16];
  static readonly EOT: number[] = [4];
  static readonly NUL: number[] = [0];
  static readonly ESC: number[] = [27];
  static readonly TAB: number[] = [116];
  static readonly EOL: number[] = [10];
  static readonly BEEP: number[] = [27, 66];
  static readonly TXT_ALIGNMENT: number[] = [27, 97];
  static readonly TXT_FONT_FAMILY: number[] = [27, 77];
}

export class BitmapFormat {
  static readonly S8: number[] = [27, 42, 0];
  static readonly D8: number[] = [27, 42, 1];
  static readonly S24: number[] = [27, 42, 32];
  static readonly D24: number[] = [27, 42, 33];
}

export enum TextAlignment {
  LeftJustification,
  Center,
  RightJustification,
}

export class Utils {
  public static isValidUInt8(value: number) {
    return value == new Uint8Array([value])[0];
  }

  public static int32ToArray(number: number): number[] {
    return [number & 0xFF,
    (number >> 8) & 0xFF,
    (number >> 16) & 0xFF,
    (number >> 24) & 0xFF];
  }

  public static int16ToArray(number: number): number[] {
    return [number & 0xFF,
    (number >> 8) & 0xFF];
  }
}

export class BarcodeQROptions {
  level: QRLevel = QRLevel.L;
  size: number = 6;

  constructor(level = QRLevel.L, size = 6) {
    this.level = level;
    this.size = size;
  }
}

export class Code2DFormat {
  static readonly TYPE_PDF417: number[] = [29, 90, 0];
  static readonly TYPE_DATAMATRIX: number[] = [29, 90, 1];
  static readonly TYPE_QR: number[] = [29, 90, 2];
  static readonly CODE2D: number[] = [27, 90];
  static readonly QR_LEVEL_L: number[] = [76];
  static readonly QR_LEVEL_M: number[] = [77];
  static readonly QR_LEVEL_Q: number[] = [81];
  static readonly QR_LEVEL_H: number[] = [72];
}

export class GSV0Format {
  static readonly NORMAL: number[] = [29, 118, 48, 0];
  static readonly DW: number[] = [29, 118, 48, 1];
  static readonly DH: number[] = [29, 118, 48, 2];
  static readonly DWDH: number[] = [29, 118, 48, 3];
}

export enum QRLevel {
  L,
  M,
  Q,
  H
}